'use client';

import { Box } from '@terra/ui-styled-system';
import {
  TraceFilesProvider,
  ChatHarProvider,
  type TraceExpandedSections,
} from '@terra/ui-design-system';
import { useMemo, useCallback, useEffect } from 'react';
import { useAppTargetId } from '../../store/projectStore';
import { useSmokeTestHistory } from '../../hooks/api/useSmokeTestHistory';
import { useTracesSSE } from '../../hooks/useTracesSSE';
import { useAgentExecutionSSE } from '../../hooks/useAgentExecutionSSE';
import { useBoundedQueryRefresh } from '../../hooks/useBoundedQueryRefresh';
import { TraceContent } from './TraceContent';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useHarById } from '../../hooks/api/useHarById';
import { useHarDisplayConfig } from '../../hooks/useHarDisplayConfig';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import { TraceViewSettingsStoreProvider } from '../../providers/TraceViewSettingsStoreProvider';
import { useTabViewStateStore } from '../../store/tabViewStateStore';
import {
  useLayoutStore,
  type ExecutionsMainContentTab,
} from '../../store/layoutStore';

type TraceDetailsTabProps = {
  tab: ExecutionsMainContentTab;
};

export function TraceDetailsTab({ tab }: TraceDetailsTabProps) {
  const appTargetId = useAppTargetId();
  const updateTab = useLayoutStore((state) => state.updateMainContentTab);

  const threadId = tab.data.threadId;
  const executionId = tab.data.executionId;
  const agentName = tab.data.agentName;

  // Get persisted view state for this trace - subscribe reactively to state changes
  const savedViewState = useTabViewStateStore((state) =>
    threadId ? state.traceStates[threadId] : undefined,
  );
  const setTraceViewState = useTabViewStateStore(
    (state) => state.setTraceViewState,
  );

  const handleExpandedSectionsChange = useCallback(
    (newSections: TraceExpandedSections) => {
      if (threadId) {
        setTraceViewState(threadId, { expandedSections: newSections });
      }
    },
    [threadId, setTraceViewState],
  );

  const handleExecutionTabChange = useCallback(
    (nextTab: string) => {
      if (threadId) {
        setTraceViewState(threadId, { executionSubTab: nextTab });
      }
    },
    [threadId, setTraceViewState],
  );

  const {
    data: smokeTestHistoryResponse,
    isLoading,
    error,
    refetch,
    invalidate,
    setStepVisibility,
  } = useSmokeTestHistory({
    threadId: threadId || '',
    enabled: !!threadId,
  });

  const refreshHistory = useBoundedQueryRefresh({
    refetch,
  });

  const handleAbortSuccess = useCallback(() => {
    return refreshHistory({
      shouldScheduleFollowUp: (history) => history?.status !== 'ABORTED',
    });
  }, [refreshHistory]);

  const handleResumeSuccess = useCallback(() => {
    return refreshHistory({
      shouldScheduleFollowUp: (history) => history?.status !== 'RUNNING',
    });
  }, [refreshHistory]);

  const handleDeleteSuccess = useCallback(() => {
    return invalidate();
  }, [invalidate]);

  useTracesSSE({
    applicationId: appTargetId || '',
    threadId,
    enabled: !!threadId && !!appTargetId,
    onTraceEvent: () => {
      refetch();
    },
  });

  useAgentExecutionSSE({
    executionId,
    targetId: appTargetId || undefined,
    enabled: !!executionId && !!appTargetId,
    onTraceEvent: () => {
      refetch();
    },
  });

  const fetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }) => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        if (response && response.hars) {
          return response.hars;
        }

        console.error('Failed to fetch HAR data:', response);

        return [];
      } catch (fetchError) {
        console.error('Error fetching HAR data:', fetchError);

        return [];
      }
    },
    [],
  );

  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(threadId);
  const harDisplay = useHarDisplayConfig(appTargetId);

  const traceFiles = useMemo(() => {
    return (
      smokeTestHistoryResponse?.files?.filter(
        (file): file is NonNullable<typeof file> => file !== null,
      ) || []
    );
  }, [smokeTestHistoryResponse]);

  const hasSmokeTestHistory = Boolean(smokeTestHistoryResponse);
  const traceTitle = useMemo(() => {
    const objective = smokeTestHistoryResponse?.signal?.description?.trim();

    return objective && objective.length > 0
      ? `Execution: ${objective}`
      : 'Execution';
  }, [smokeTestHistoryResponse?.signal?.description]);

  // Update tab name to "Execution: [objective]" when trace/signal data loads
  useEffect(() => {
    if (!hasSmokeTestHistory || !tab.id) {
      return;
    }

    updateTab(tab.id, { title: traceTitle });
  }, [hasSmokeTestHistory, tab.id, traceTitle, updateTab]);

  return (
    <Box h="full" w="full">
      <TraceViewSettingsStoreProvider>
        <ChatHarProvider
          threadId={threadId ?? null}
          fetchHarData={fetchHarData}
          fetchHarById={fetchHarById}
          hasHarPresence={hasHarPresence}
          harDisplay={harDisplay}
        >
          <TraceFilesProvider traceFiles={traceFiles}>
            <TraceContent
              applicationId={appTargetId ?? ''}
              threadId={threadId}
              history={smokeTestHistoryResponse}
              isLoading={isLoading}
              error={error}
              agentName={agentName}
              executionId={tab.data.executionId}
              expandedSections={savedViewState?.expandedSections}
              onExpandedSectionsChange={handleExpandedSectionsChange}
              executionTab={savedViewState?.executionSubTab ?? 'overview'}
              onExecutionTabChange={handleExecutionTabChange}
              onAbortSuccess={handleAbortSuccess}
              onResumeSuccess={handleResumeSuccess}
              onDeleteSuccess={handleDeleteSuccess}
              onStepVisibilityChange={setStepVisibility}
            />
          </TraceFilesProvider>
        </ChatHarProvider>
      </TraceViewSettingsStoreProvider>
    </Box>
  );
}
